import React, { useEffect, useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import DocumentEdit from './DocumentEdit';
import AgreementUpload from '../../components/ProjectScreens/AgreementUpload';
import { useUser } from '../../context/UserContext';
import axiosInstance from '../../services/api/axios';
import { parse } from 'node-html-parser';
import { CloseButton, PrimaryButton } from '../../components/Buttons';
import CustomInput from '../../components/CustomInput';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const documentTypes = [
  { type: 'BOOKING_LETTER', title: 'Booking Receipt', subtitle: 'Letter', description: 'Thank you for booking. Amount: ₹50,000...' },
  { type: 'CANCELLATION_LETTER', title: 'Cancellation Letter', subtitle: 'Letter', description: 'Acknowledge a booking cancellation.' },
  { type: 'PROPERTY_HANDOVER_LETTER', title: 'Property Handover', subtitle: 'Letter', description: 'Confirm property handover to customer.' },
  { type: 'INTERIOR_EXECUTION_AGREEMENT', title: 'Interior Design & Execution Agreement', subtitle: 'Agreement', description: 'N-party interior agreement (Developer, BuildVizer, Buyer(s)); eSigned.' },
 // { type: 'COST_SHEET_NOTIFICATION', title: 'Cost Sheet Notification', subtitle: 'Notification', description: 'Notify about cost sheet details.' },
  { type: 'CANCELLATION_REFUND', title: 'Cancellation Refund', subtitle: 'Receipt', description: 'Receipt for refund after cancellation.' },
  { type: 'TOKEN_PAYMENT', title: 'Token Payment', subtitle: 'Receipt', description: 'Receipt for token payment.' },
  { type: 'DOWN_PAYMENT', title: 'Down Payment', subtitle: 'Receipt', description: 'Receipt for down payment.' },
  { type: 'SCHEDULED_PAYMENT', title: 'Scheduled Payment', subtitle: 'Receipt', description: 'Receipt for scheduled payment.' },
  { type: 'FULL_PAYMENT', title: 'Full Payment', subtitle: 'Receipt', description: 'Receipt for full payment.' },
  { type: 'SLAB_WISE_PAYMENT', title: 'Slab Wise Payment', subtitle: 'Receipt', description: 'Receipt for slab-wise payment.' },
  { type: 'SCHEDULED_DEMAND_LETTERS_USER', title: 'User Scheduled Demand Letter ', subtitle: 'Demand Letter', description: 'Demand letter for scheduled payments to user.' },
  { type: 'SCHEDULED_DEMAND_LETTERS_BANK', title: 'Bank Scheduled Demand Letter ', subtitle: 'Demand Letter', description: 'Demand letter for scheduled payments to bank.' },
  { type: 'SCHEDULED_DEMAND_LETTERS_POST', title: 'Post Scheduled Demand Letter ', subtitle: 'Demand Letter', description: 'Demand letter for scheduled payments via post.' },
  { type: 'SLAB_WISE_DEMAND_LETTERS_POST', title: 'Post Slab Wise Demand Letter ', subtitle: 'Demand Letter', description: 'Demand letter for slab-wise payments via post.' },
  { type: 'SLAB_WISE_DEMAND_LETTERS_USER', title: 'User Slab Wise Demand Letter ', subtitle: 'Demand Letter', description: 'Demand letter for slab-wise payments to user.' },
  { type: 'SLAB_WISE_DEMAND_LETTERS_BANK', title: 'Bank Slab Wise Demand Letter ', subtitle: 'Demand Letter', description: 'Demand letter for slab-wise payments to bank.' },
];

// Skeleton component for document cards
const DocumentCardSkeleton = () => (
  <div className="group relative bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
    {/* Document Preview Header Skeleton */}
    <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 p-6 flex items-center justify-center">
      <Skeleton circle height={64} width={64} />
    </div>

    {/* Document Content Skeleton */}
    <div className="p-5">
      <div className="mb-3">
        <Skeleton height={20} width="80%" className="mb-1" />
        <Skeleton height={12} width="40%" />
      </div>

      <div className="mb-4">
        <Skeleton height={14} width="100%" className="mb-1" />
        <Skeleton height={14} width="75%" />
      </div>

      <Skeleton height={40} width="100%" />
    </div>
  </div>
);

const Documents = ({ projectId }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentData, setDocumentData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadedAgreement, setUploadedAgreement] = useState(null);
  const [allDocuments, setAllDocuments] = useState([]);
  const [loadingDocument, setLoadingDocument] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const { userInfo } = useUser();

  const formatDocumentType = (type) => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Filter documents based on search query
  const filteredDocuments = documentTypes.filter(doc => {
    const query = searchQuery.toLowerCase();
    return (
      doc.title.toLowerCase().includes(query) ||
      doc.subtitle.toLowerCase().includes(query) ||
      doc.description.toLowerCase().includes(query) ||
      doc.type.toLowerCase().includes(query)
    );
  });

  const handleAgreementChange = (base64) => {
    setUploadedAgreement(base64);
  };

  // Prefetch all documents for the given customer and project
  useEffect(() => {
    const customerId = userInfo?.loginIdentifierDetails?.customer?.customerId;
    if (!customerId || !projectId) {
      setIsInitialLoading(false);
      return;
    }

    let isActive = true;
    (async () => {
      try {
        const response = await axiosInstance.get('/customers/document', {
          params: { customerId, projectId }
        });
        if (isActive) {
          setAllDocuments(Array.isArray(response) ? response : []);
        }
      } catch (error) {
        console.error('Error prefetching documents:', error);
      } finally {
        if (isActive) {
          setIsInitialLoading(false);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [userInfo, projectId]);

  const handleViewDocument = async (documentType) => {
    setLoadingDocument(documentType);
    try {
      // Try to find the document from prefetched list first
      const localDoc = allDocuments.find((d) => d?.documentType === documentType);

      let document = localDoc;

      // Fallback to API if not found locally
      if (!document) {
        const response = await axiosInstance.get('/customers/document', {
          params: {
            customerId: userInfo?.loginIdentifierDetails?.customer?.customerId,
            documentType: documentType,
            projectId: projectId
          }
        });
        document = Array.isArray(response) ? response[0] : response;
      }

      const originalHtml = document?.documentHtmlBytes || '';
      let editableContent = '';
      try {
        const root = parse(originalHtml);
        const container = root.querySelector('.container');
        if (container) {
          // Extract only the content, not any nested HTML structure
          let content = container.innerHTML;
          
          // Remove any nested HTML document structure
          content = content
            .replace(/<!DOCTYPE[^>]*>/gi, '')
            .replace(/<html[^>]*>/gi, '')
            .replace(/<\/html>/gi, '')
            .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
            .replace(/<body[^>]*>/gi, '')
            .replace(/<\/body>/gi, '')
            .trim();
          
          editableContent = content;
        } else {
          editableContent = originalHtml;
        }
      } catch (parseError) {
        editableContent = originalHtml;
      }
      setDocumentData({
        content: editableContent,
        originalContent: originalHtml,
        documentId: document?.documentId,
        documentType: document?.documentType
      });
      setSelectedDocument(documentType);
      setIsModalVisible(true);
    } catch (error) {
      console.error('Error fetching document:', error);
    } finally {
      setLoadingDocument(null);
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedDocument(null);
    setDocumentData(null);
  };

  const handleDocumentSaveSuccess = (updatedDoc) => {
    if (!updatedDoc?.documentId) return;

    setAllDocuments((prevDocs) =>
      (prevDocs || []).map((doc) =>
        doc?.documentId === updatedDoc.documentId
          ? { ...doc, documentHtmlBytes: updatedDoc.documentHtmlBytes }
          : doc
      )
    );

    setDocumentData((prev) =>
      prev && prev.documentId === updatedDoc.documentId
        ? {
            ...prev,
            originalContent: updatedDoc.documentHtmlBytes,
          }
        : prev
    );
  };

  const agreementProps = {
    initialAgreement: uploadedAgreement,
    onAgreementChange: handleAgreementChange,
    projectId: projectId,
  };

  return (
    <div className="w-full flex lg:flex-row flex-col gap-4">
      {/* Left side - Document cards */}
      <div className="lg:w-2/3 w-full space-y-4">
        {/* Header card */}
        <div className="rounded-2xl bg-white shadow-sm border border-slate-200 p-4 lg:p-5">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-base lg:text-lg font-semibold text-slate-900">Documents</h1>
              <p className="text-xs text-slate-500 mt-0.5">
                {isInitialLoading
                  ? "Loading…"
                  : `${filteredDocuments.length} of ${documentTypes.length} documents`}
                {!isInitialLoading && searchQuery ? ` matching "${searchQuery}"` : null}
              </p>
            </div>
            <div className="w-full sm:w-72">
              {isInitialLoading ? (
                <Skeleton height={40} width="100%" />
              ) : (
                <CustomInput
                  type="text"
                  className="w-full"
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSearchQuery(e.target.value);
                  }}
                />
              )}
            </div>
          </div>
        </div>

        <div className="px-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {isInitialLoading ? (
              // Show skeleton loading while initial data is being fetched
              Array.from({ length: 6 }).map((_, idx) => (
                <DocumentCardSkeleton key={idx} />
              ))
            ) : (
              filteredDocuments.map((doc, idx) => (
                <div
                  key={doc.type}
                  className="group relative bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 border border-slate-200 hover:border-flamingo-200 hover:-translate-y-0.5 overflow-hidden"
                >
                  {/* Document Preview Header — slate gradient with the
                      document icon centred. */}
                  <div className="relative bg-gradient-to-br from-slate-50 to-slate-100 p-6 flex items-center justify-center group-hover:from-flamingo-50 group-hover:to-slate-50 transition-colors">
                    <img
                      src={require('../../assets/images/image.png')}
                      alt="PDF Document"
                      className="w-16 h-16 object-contain opacity-80 group-hover:opacity-100 transition-opacity duration-200"
                      draggable={false}
                    />
                  </div>

                  {/* Document Content */}
                  <div className="p-5">
                    <div className="mb-3">
                      <h3 className="font-semibold text-slate-900 text-base mb-1 line-clamp-1 group-hover:text-flamingo-700 transition-colors">
                        {doc.title}
                      </h3>
                      <p className="text-[11px] text-slate-500 uppercase tracking-wide font-medium">
                        {doc.subtitle}
                      </p>
                    </div>

                    {/* <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                      {doc.description}
                    </p> */}

                    <PrimaryButton
                      className="w-full selection:font-medium py-2.5 px-4  transition-colors duration-200"
                      onClick={() => handleViewDocument(doc.type)}
                      disabled={loadingDocument === doc.type}
                    >
                      {loadingDocument === doc.type ? (
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Loading...
                        </div>
                      ) : (
                        "View Document"
                      )}
                    </PrimaryButton>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* No results message */}
          {filteredDocuments.length === 0 && searchQuery && (
            <div className="text-center py-12">
              <div className="text-gray-500">
                <Search className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium text-gray-600 mb-2">No documents found</p>
                <p className="text-sm text-gray-500">
                  No documents match your search for "{searchQuery}"
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right side - Agreement Upload */}
      <div className="lg:w-1/3 w-full">
      <h1 className="text-2xl font-bold mb-2 text-gray-800">Agreement Upload</h1>
      <p className="text-gray-600 mb-4">
        Upload and manage project agreements and legal documents.
      </p>
      <div className="space-y-4">
          <AgreementUpload {...agreementProps} />
        </div>
      </div>

      {isModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[97%] max-w-[1700px] h-[97vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">
                  {formatDocumentType(selectedDocument)}
                </h2>
                <p className="text-sm text-gray-500">Edit and preview your document</p>
              </div>
              <CloseButton
                onClick={handleModalClose}
              >
              </CloseButton>
            </div>
            <div className="flex-1 min-h-0 flex flex-col">
              <div className="p-4 flex-1 min-h-0 flex flex-col">
                {selectedDocument && (
                  <DocumentEdit
                    documentType={selectedDocument}
                    documentData={documentData}
                    onClose={handleModalClose}
                    onSaveSuccess={handleDocumentSaveSuccess}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;